import React, { useEffect, useMemo, useState } from 'react';
import {
    Badge,
    Button,
    Form,
    InputGroup,
    Modal,
    Spinner,
} from 'react-bootstrap';
import API from '../../../api/axios';

const AddShopModal = ({
    show,
    onClose,
    onAdded,
    existingShopIds = [],
}) => {
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [results, setResults] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());

    useEffect(() => {
        if (!show) return;
        setSearch('');
        setResults([]);
        setSelectedIds(new Set());
        setErrorMsg('');
    }, [show]);

    const doSearch = async () => {
        const q = String(search || '').trim();
        if (!q) return;

        try {
            setLoading(true);
            setErrorMsg('');
            const url = `/shop?page=1&sortBy=name&order=asc&name=${encodeURIComponent(q)}`;
            const { data } = await API.get(url);

            const items = (data && (data.items || data.data)) || (Array.isArray(data) ? data : []);
            const normalized = (items || [])
                .map((s) => ({
                    id: s?.id ?? s?.shopId ?? '',
                    name: s?.name ?? s?.shopName ?? 'Shop',
                    email: s?.email ?? '',
                }))
                .filter((x) => x.id);

            setResults(normalized);
        } catch (e) {
            const msg =
                (e && e.response && e.response.data && e.response.data.message) ||
                (e && e.message) ||
                'Failed to search shops';
            setErrorMsg(msg);
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const toggleSelect = (shopId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(shopId)) next.delete(shopId);
            else next.add(shopId);
            return next;
        });
    };

    const handleAdd = async () => {
        const ids = Array.from(selectedIds).filter(Boolean);
        if (ids.length === 0) return;
        await onAdded(ids);
        onClose();
    };

    const disabledSet = useMemo(() => new Set(existingShopIds || []), [existingShopIds]);

    return (
        <Modal show={show} onHide={onClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Add participating shops</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <InputGroup className="mb-3">
                    <Form.Control
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search shop name..."
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') doSearch();
                        }}
                    />
                    <Button
                        variant="outline-secondary"
                        onClick={doSearch}
                        disabled={loading || !String(search || '').trim()}
                    >
                        {loading ? 'Searching...' : 'Search'}
                    </Button>
                </InputGroup>

                {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}

                <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                    {loading ? (
                        <div className="text-muted d-flex align-items-center gap-2">
                            <Spinner size="sm" /> Loading...
                        </div>
                    ) : results.length === 0 ? (
                        <div className="text-muted">No results.</div>
                    ) : (
                        results.map((s) => {
                            const already = disabledSet.has(s.id);
                            const checked = selectedIds.has(s.id);

                            return (
                                <div key={s.id} className="d-flex align-items-start justify-content-between border rounded p-2 mb-2">
                                    <div className="d-flex flex-column">
                                        <div className="fw-semibold">{s.name}</div>
                                        <div className="text-muted small">{s.email || '—'}</div>
                                    </div>

                                    <div className="d-flex align-items-center gap-2">
                                        {already ? (
                                            <Badge bg="light" text="dark" className="border">Already added</Badge>
                                        ) : (
                                            <Form.Check
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleSelect(s.id)}
                                                label=""
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose}>Cancel</Button>
                <Button
                    className="btn-custom-success border"
                    onClick={handleAdd}
                    disabled={selectedIds.size === 0}
                >
                    Add selected
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddShopModal;